/**
 * Triveny.ai — User Preferences
 *
 * Replaces the WeaveIn UserPreferences component for Triveny users. The
 * WeaveIn page is keyed on the AuthContext `preferences` object loaded from
 * GET /api/user/preferences, which is a healthcare endpoint and 401s for
 * platform JWTs — so on Triveny it renders mostly empty / broken.
 *
 * This component shows only the surfaces that make sense for a platform
 * tenant user:
 *   - General: language, theme (dark/light/system), timezone — stored in
 *     localStorage (client-only; no backend write yet)
 *   - Accessibility: text scale, contrast mode, focus style — driven by the
 *     existing useAccessibility() context (already client-side localStorage)
 *   - Security: TOTP enrollment status from /api/platform/auth/2fa/methods,
 *     trusted-devices count, link out to /setup-2fa to enroll/disable
 *
 * Surfaces deliberately omitted (vs WeaveIn):
 *   - GeoTracking / location consent — healthcare-only
 *   - Personal Connected Services — healthcare-only
 *   - Notification preferences detail (workflows / communications / tasks)
 *     because the backend endpoints are healthcare-only. Desktop-notification
 *     permission is client-side so it's kept.
 */

import { useTabbedChrome } from '@/components/weavein';
import React, { useEffect, useState } from 'react';
import {
  Sun,
  Moon,
  Monitor,
  Bell,
  BellOff,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Smartphone,
  Type,
  Contrast,
  MousePointer,
  Languages,
  Globe,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import { config } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useAccessibility } from '@/contexts/AccessibilityContext';
import type { TextScale, ContrastMode, FocusStyle } from '@/contexts/AccessibilityContext';
import { LanguageSelector } from '@/components/LanguageSelector';
import { TimezoneSelector } from '@/components/TimezoneSelector';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { desktopNotifications, NotificationPermissionState } from '@/services/desktopNotifications';

interface TwoFactorMethodsResponse {
  mfa_enabled: boolean;
  mfa_enrolled_at?: string | null;
  backup_codes_remaining?: number;
  trusted_devices_count?: number;
}

export interface TrivenyUserPreferencesProps {
  defaultTab?: string;
  onNavigate?: (tab: string) => void;
}

// localStorage keys — keep aligned with WeaveIn UserPreferences for any
// cross-product session that happens to land on either UI.
const LS_THEME = 'triveny_theme';
const LS_TIMEZONE = 'triveny_timezone';

function applyThemeToDocument(theme: 'light' | 'dark' | 'system') {
  if (typeof window === 'undefined') return;
  const resolved =
    theme === 'system'
      ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
      : theme;
  document.documentElement.classList.toggle('dark', resolved === 'dark');
  document.documentElement.dataset.theme = resolved;
}

export default function TrivenyUserPreferences({
  defaultTab = 'general',
  onNavigate,
}: TrivenyUserPreferencesProps = {}) {
  const tabbed = useTabbedChrome();
  const { language: currentLanguage } = useAppTranslation();
  const { settings: accessibilitySettings, updateSettings: updateAccessibilitySettings } = useAccessibility();

  // -------- General --------
  const [theme, setTheme] = useState<'light' | 'dark' | 'system'>(() => {
    if (typeof window === 'undefined') return 'light';
    return (localStorage.getItem(LS_THEME) as 'light' | 'dark' | 'system') || 'light';
  });
  const [timezone, setTimezone] = useState<string>(() => {
    if (typeof window === 'undefined') return 'America/Los_Angeles';
    return localStorage.getItem(LS_TIMEZONE) || 'America/Los_Angeles';
  });

  useEffect(() => {
    applyThemeToDocument(theme);
    localStorage.setItem(LS_THEME, theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem(LS_TIMEZONE, timezone);
  }, [timezone]);

  // -------- Desktop notification permission (client-side only) --------
  const [permissionState, setPermissionState] = useState<NotificationPermissionState>('default');
  const [requestingPermission, setRequestingPermission] = useState(false);

  useEffect(() => {
    if (desktopNotifications.isNotificationSupported()) {
      setPermissionState(desktopNotifications.getPermissionState());
    }
  }, []);

  const handleRequestPermission = async () => {
    setRequestingPermission(true);
    try {
      const granted = await desktopNotifications.requestPermission();
      setPermissionState(granted ? 'granted' : 'denied');
    } finally {
      setRequestingPermission(false);
    }
  };

  // -------- Security: TOTP status --------
  const [twoFactor, setTwoFactor] = useState<TwoFactorMethodsResponse | null>(null);
  const [twoFactorLoading, setTwoFactorLoading] = useState(true);
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) {
          setTwoFactorLoading(false);
          return;
        }
        const res = await fetch(`${config.weaveinApi.baseUrl}/api/platform/auth/2fa/methods`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as TwoFactorMethodsResponse;
        if (!cancelled) setTwoFactor(data);
      } catch (e: any) {
        if (!cancelled) setTwoFactorError(e?.message || 'Failed to load 2FA status');
      } finally {
        if (!cancelled) setTwoFactorLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--v2-paper, #fafaf7)', padding: '24px 28px' }}>
      <div style={{ maxWidth: 880, margin: '0 auto' }}>
        <header style={{ marginBottom: 18 }}>
          {!tabbed && (
          <h1 style={{ fontFamily: "'Inter Tight', system-ui, sans-serif", fontSize: 24, fontWeight: 600, margin: 0, color: 'var(--v2-ink, #14171c)', letterSpacing: '-0.02em' }}>
            Preferences
          </h1>
          )}
          <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--v2-ink-500, #6b7280)' }}>
            Language, theme, accessibility, and account security for your Triveny workspace.
          </p>
        </header>

        <Tabs defaultValue={defaultTab}>
          <TabsList>
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="accessibility">Accessibility</TabsTrigger>
            <TabsTrigger value="notifications">Notifications</TabsTrigger>
            <TabsTrigger value="security">Security</TabsTrigger>
          </TabsList>

          {/* ────────────────────────── General ────────────────────────── */}
          <TabsContent value="general" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Languages size={18} /> Language
                </CardTitle>
                <CardDescription>
                  Set the language used across the Triveny UI. Stored locally — applies to your browser.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <LanguageSelector />
                <p className="mt-2 text-xs text-gray-500">Current: {currentLanguage}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sun size={18} /> Theme
                </CardTitle>
                <CardDescription>
                  Light, dark, or follow your OS. Triveny's design tokens flip automatically.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  {([
                    { v: 'light', Icon: Sun, label: 'Light' },
                    { v: 'dark', Icon: Moon, label: 'Dark' },
                    { v: 'system', Icon: Monitor, label: 'System' },
                  ] as const).map(({ v, Icon, label }) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setTheme(v)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm ${
                        theme === v
                          ? 'border-gray-900 bg-gray-900 text-white'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-500'
                      }`}
                    >
                      <Icon size={14} /> {label}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe size={18} /> Timezone
                </CardTitle>
                <CardDescription>
                  Times in My Day, activities, and campaigns render in this zone.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <TimezoneSelector value={timezone} onChange={setTimezone} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─────────────────────── Accessibility ─────────────────────── */}
          <TabsContent value="accessibility" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Type size={18} /> Text size
                </CardTitle>
                <CardDescription>Scale text from 90% to 200% of default.</CardDescription>
              </CardHeader>
              <CardContent>
                <Slider
                  min={90}
                  max={200}
                  step={10}
                  value={[accessibilitySettings.textScale]}
                  onValueChange={([v]) => updateAccessibilitySettings({ textScale: v as TextScale })}
                />
                <p className="mt-2 text-xs text-gray-500">{accessibilitySettings.textScale}%</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Contrast size={18} /> Contrast mode
                </CardTitle>
                <CardDescription>
                  High contrast for low-vision users, or invert for sensitivity to bright UIs.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 flex-wrap">
                  {(['default', 'high', 'inverted'] as ContrastMode[]).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => updateAccessibilitySettings({ contrastMode: mode })}
                      className={`px-3 py-2 rounded-md border text-sm capitalize ${
                        accessibilitySettings.contrastMode === mode
                          ? 'border-gray-900 bg-gray-900 text-white'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-500'
                      }`}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MousePointer size={18} /> Focus indicator
                </CardTitle>
                <CardDescription>
                  Make keyboard-focus rings more visible for tab-based navigation.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 flex-wrap">
                  {(['default', 'high-visibility', 'thick'] as FocusStyle[]).map((style) => (
                    <button
                      key={style}
                      type="button"
                      onClick={() => updateAccessibilitySettings({ focusStyle: style })}
                      className={`px-3 py-2 rounded-md border text-sm ${
                        accessibilitySettings.focusStyle === style
                          ? 'border-gray-900 bg-gray-900 text-white'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-500'
                      }`}
                    >
                      {style.replace('-', ' ')}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Motion</CardTitle>
                <CardDescription>Reduce animations and transitions.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Reduce motion</span>
                  <Switch
                    checked={accessibilitySettings.reduceMotion}
                    onCheckedChange={(v) => updateAccessibilitySettings({ reduceMotion: v })}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ────────────────────── Notifications ────────────────────── */}
          <TabsContent value="notifications" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell size={18} /> Desktop notifications
                </CardTitle>
                <CardDescription>
                  Show OS-level toasts for new activity. Browser permission required.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!desktopNotifications.isNotificationSupported() ? (
                  <p className="text-sm text-gray-500">
                    Your browser does not support desktop notifications.
                  </p>
                ) : permissionState === 'granted' ? (
                  <div className="flex items-center gap-2 text-sm text-emerald-700">
                    <Bell size={14} /> Enabled
                  </div>
                ) : permissionState === 'denied' ? (
                  <div className="flex items-center gap-2 text-sm text-amber-700">
                    <BellOff size={14} /> Blocked — re-enable via your browser site settings.
                  </div>
                ) : (
                  <Button onClick={handleRequestPermission} disabled={requestingPermission}>
                    {requestingPermission ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Enable browser notifications
                  </Button>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>In-app notification preferences</CardTitle>
                <CardDescription>
                  Per-channel notification toggles will land here once the platform notifications
                  backend is wired (placeholder for now).
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-500">
                  Coming soon — the unified inbox (linked from your home top nav) is the canonical
                  surface for in-app messages today.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ───────────────────────── Security ───────────────────────── */}
          <TabsContent value="security" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield size={18} /> Two-factor authentication
                </CardTitle>
                <CardDescription>
                  Triveny requires TOTP for all platform users. Status reflects your current
                  enrollment on this account.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {twoFactorLoading ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading status…
                  </div>
                ) : twoFactorError ? (
                  <div className="text-sm text-red-600">{twoFactorError}</div>
                ) : twoFactor?.mfa_enabled ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-emerald-700">
                      <ShieldCheck size={16} /> Enrolled
                      {twoFactor.mfa_enrolled_at && (
                        <span className="text-gray-500 ml-1">
                          since {new Date(twoFactor.mfa_enrolled_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-600 grid grid-cols-2 gap-2 max-w-sm">
                      <div>
                        Backup codes remaining:{' '}
                        <strong>{twoFactor.backup_codes_remaining ?? 0}</strong>
                      </div>
                      <div className="flex items-center gap-1">
                        <Smartphone size={12} />
                        Trusted devices:{' '}
                        <strong>{twoFactor.trusted_devices_count ?? 0}</strong>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => (window.location.href = '/setup-2fa')}
                      >
                        Manage <ExternalLink size={14} className="ml-1" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-amber-700">
                      <ShieldAlert size={16} /> Not enrolled
                    </div>
                    <Button onClick={() => (window.location.href = '/setup-2fa')}>
                      Enroll TOTP <ExternalLink size={14} className="ml-1" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
